<script setup lang="ts">
import type { CheckboxRootEmits, CheckboxRootProps } from 'radix-vue';
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'radix-vue';
import { PhCheck, PhMinus } from '@phosphor-icons/vue';
import { computed } from 'vue';
import { CheckboxUIRecipe } from './CheckboxUiStyles';
import { cn } from '../../../lib/utils';

interface CheckboxUiProps extends CheckboxRootProps {
  /** Renders the Error variant — a negative border on an otherwise unchecked box. */
  error?: boolean;
}

const props = defineProps<CheckboxUiProps>();
const emits = defineEmits<CheckboxRootEmits>();

/** `error` is ours, not radix's — keep it off the underlying element. */
const delegatedProps = computed(() => {
  const { error: _error, ...rest } = props;
  return rest;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const checkedValue = computed(() => {
  const typedProps = props as CheckboxRootProps & {
    checked?: boolean | 'indeterminate';
    modelValue?: boolean | 'indeterminate';
  };
  return typedProps.checked ?? typedProps.modelValue;
});

const isIndeterminate = computed(() => checkedValue.value === 'indeterminate');
</script>

<template>
  <CheckboxRoot
    v-bind="forwarded"
    :class="cn(
      CheckboxUIRecipe({
        error: props.error ?? false
      }),
      $attrs.class ?? ''
    )"
    :name="props.name || props.id"
  >
    <CheckboxIndicator class="flex h-full w-full items-center justify-center">
      <PhMinus v-if="isIndeterminate" :size="16" weight="bold" />
      <PhCheck v-else :size="16" weight="bold" />
    </CheckboxIndicator>
  </CheckboxRoot>
</template>
